import { type RefObject, useCallback, useRef } from "react";

const DEFAULT_DRAG_THRESHOLD = 5;

type UseHorizontalDragScrollResult = {
	scrollRef: RefObject<HTMLDivElement | null>;
	onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
	wasDragged: () => boolean;
};

export const useHorizontalDragScroll = (
	dragThreshold: number = DEFAULT_DRAG_THRESHOLD,
): UseHorizontalDragScrollResult => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const dragStartX = useRef(0);
	const scrollStartLeft = useRef(0);
	const isDragging = useRef(false);
	const hasDragged = useRef(false);

	const onMouseDown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const el = scrollRef.current;
			if (!el) return;
			isDragging.current = true;
			hasDragged.current = false;
			dragStartX.current = e.clientX;
			scrollStartLeft.current = el.scrollLeft;
			el.style.cursor = "grabbing";
			el.style.userSelect = "none";

			const handleMouseMove = (ev: MouseEvent) => {
				if (!isDragging.current) return;
				const delta = ev.clientX - dragStartX.current;
				if (Math.abs(delta) > dragThreshold) hasDragged.current = true;
				el.scrollLeft = scrollStartLeft.current - delta;
			};

			const handleMouseUp = () => {
				isDragging.current = false;
				el.style.cursor = "grab";
				el.style.userSelect = "";
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		},
		[dragThreshold],
	);

	const wasDragged = useCallback(() => hasDragged.current, []);

	return { scrollRef, onMouseDown, wasDragged };
};
